import * as React from 'react';
import { connect } from 'react-redux';
import { HashRouter as Router, Redirect, Switch } from 'react-router-dom';
import { AnalyticsRoute } from '@chr/online-shared-ui-analytics';
import { IAppState } from '@/app-state';
import * as UserHelpers from '@/app/common/user-helpers';
import './app.component.scss';
import { ConfigurationService } from '@/config/configuration-service';
import { ErrorHandler } from '@/app/common/components';
import { ConnectedConversation } from '@/app/common/conversation/conversation';
import { UserProfile, getAxiosInstance } from '@chr/online-shared-microsite-core';
import { AccessPolicy } from '@chr/online-shared-microsite-core/types';
import { initializeGlobalPermissions } from './common/global-permission';
import { getUserRequest } from './common/user/actions';
import { EntityType } from '@/app-constants';
import { ConnectedConversationContainer } from './common/conversation/conversation-container';
import { IEBannerWarning, LoadingIndicator, Sizes } from '@chr/navisphereonline-shared-components';
import { ChrAuthClient } from '@chr/common-web-ui-authentication/authClient';
import { Header } from './common/components/header';
import { getFeatureFlags } from './common/featureFlags/featureFlagsActions';
import { PermissionIds } from '@chr/online-shared-ui-constants';
import { Suspense } from 'react';

const ConnectedPoDetails = React.lazy(() => import('@/app/areas/unacknowledged-po/details').then(module => ({ default: module.ConnectedPoDetails })));
const ConnectedPoListPage = React.lazy(() => import('@/app/areas/unacknowledged-po/list/index').then(module => ({ default: module.ConnectedPoListPage })));
const ConnectedPurchaseOrderHistory = React.lazy(() => import('@/app/areas/unacknowledged-po/details/components/purchase-order-history/purchase-order-history').then(module => ({ default: module.ConnectedPurchaseOrderHistory })));

interface IAppDispatchOwnProps {
  getUser: () => void;
  getFeatureFlags: (userProfile: UserProfile) => any;
}

interface IAppComponentStateProps {
  entityType: EntityType,
  isSupplier: boolean;
  userAccessPolicies: AccessPolicy[],
  reduxErrors: (string | Error)[];
  showConversation: boolean;
  appConfig?: AppConfig;
  authClient?: ChrAuthClient;
  userProfile?: UserProfile;
}

interface IAppComponentOwnProps {
  appConfig?: AppConfig;
  authClient?: ChrAuthClient;
  userProfile?: UserProfile;
}

export interface IAppComponentState {
  environment: string;
  error: Error;
  errorInfo: React.ErrorInfo;
  reduxError: Error;
  debugMode: boolean;
  retry: boolean;
}

export type IAppComponentProps = IAppComponentStateProps & IAppComponentOwnProps & IAppDispatchOwnProps;

export class App extends React.Component<IAppComponentProps, IAppComponentState>
{
  state = {
    error: null as Error,
    errorInfo: null as React.ErrorInfo,
    debugMode: false,
    environment: '',
    reduxError: null,
    retry: false
  };

  async componentDidMount() {
    if (this.props.isSupplier === null) {
      this.props.getUser();
    }

    const {
      userProfile,
    } = this.props;

    this.props.getFeatureFlags(userProfile);

    const { poApiUri, debugMode, environment } = await ConfigurationService.getConfiguration();
    this.setState({ debugMode, environment });

    const axiosInstance = await getAxiosInstance();
    const customerCodes = UserHelpers.getUserCustomerCodes(this.props.userAccessPolicies);
    const getConfigurationUri = `${poApiUri}/Customers/NegotiationConfiguration/Search`;
    const responseData = await axiosInstance.post(getConfigurationUri, { customerCodes });
    initializeGlobalPermissions({ ...responseData.data });
  }

  renderRedirector = () => {
    if (this.props.isSupplier) {
      return <Redirect to="/order-confirmation/new" />;
    }

    return <Redirect to="/order-confirmation/buyer-review" />;
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ error, errorInfo });
  }

  getCurrentError = () => {
    if (this.state.retry) {
      this.setState({ retry: false });
      return null;
    }

    const error = this.state.error;
    let reduxError = null;

    reduxError = this.props.reduxErrors.filter((err) => {
      return err ? true : false; // todo catch else
    })[0];

    if (reduxError && reduxError !== this.state.reduxError) {
      this.setState({ reduxError });
    }

    return reduxError || error;
  }

  retryLoad = () => {
    this.setState({ retry: true });
  }

  showConnectedConversation() {
    if (this.props.showConversation) {
      if (this.props.entityType === EntityType.PurchaseOrder) {
        return (
          <div className={`col-xs-3 no-padding conversationSectionWrapper`}>
            <ConnectedConversationContainer />
          </div>
        );
      } else {
        return (
          <div className={`col-xs-3 no-padding conversationSectionWrapper`}>
            <ConnectedConversation />
          </div>
        );
      }
    }
    return null;
  }

  render() {
    try {
      decodeURIComponent(window.location.href);
    } catch {
      window.location.hash = '';
    }

    const {
      isSupplier,
      appConfig,
      authClient,
      showConversation,
    } = this.props;

    return isSupplier !== null ? (
      <ErrorHandler
        error={this.getCurrentError()}
        onRetryClick={this.retryLoad}
      >
        <Header appConfig={appConfig} authClient={authClient} />
        <div style={{ display: "flex", alignItems: "stretch" }}>
          <div
            className={`no-padding ${
              showConversation ? "col-xs-9" : "col-xs-12"
            }`}
          >
            <IEBannerWarning />
            <Router>
              <div className="site">
                <Suspense fallback={<LoadingIndicator size={Sizes.Small} />}>
                  <Switch>
                    <AnalyticsRoute
                      path="/order-confirmation/:bucketType"
                      strict={true}
                      exact={true}
                      component={ConnectedPoListPage}
                      pageName={"PO Management | Purchase Order Search"}
                      pagePermissions={[
                        PermissionIds.PURCHASE_ORDER_CONFIRMATION_BUYER,
                        PermissionIds.PURCHASE_ORDER_CONFIRMATION_SUPPLIER,
                      ]}
                    />
                    <AnalyticsRoute
                      path="/order-confirmation/detail/:poId/purchase-order-history"
                      strict={true}
                      exact={true}
                      component={ConnectedPurchaseOrderHistory}
                      pageName={"PO Management | Purchase Order History"}
                      pagePermissions={[
                        PermissionIds.PURCHASE_ORDER_CONFIRMATION_BUYER,
                        PermissionIds.PURCHASE_ORDER_CONFIRMATION_SUPPLIER,
                      ]}
                    />
                    <AnalyticsRoute
                      path="/order-confirmation/detail/:poId"
                      strict={true}
                      exact={true}
                      component={ConnectedPoDetails}
                      pageName={"PO Management | Purchase Order Details"}
                      pagePermissions={[
                        PermissionIds.PURCHASE_ORDER_CONFIRMATION_BUYER,
                        PermissionIds.PURCHASE_ORDER_CONFIRMATION_SUPPLIER,
                      ]}
                    />
                    <AnalyticsRoute
                      path="/"
                      render={this.renderRedirector}
                      pageName={"Customer Apps | Page Not Found"}
                    />
                  </Switch>
                </Suspense>
              </div>
            </Router>
            {/* <BackToTopButton style={{ marginRight: showConversation ? '25%' : 0 }} /> */}
          </div>
          {this.showConnectedConversation()}
        </div>
      </ErrorHandler>
    ) : null;
  }
}

const mapStateToProps = (state: IAppState, ownProps: IAppComponentOwnProps): IAppComponentStateProps => ({
  entityType: state.conversationState.entityType,
  isSupplier: state.userState.isSupplier,
  appConfig: ownProps.appConfig,
  authClient: ownProps.authClient,
  userProfile: ownProps.userProfile,
  showConversation: state.conversationState.showConversation,
  userAccessPolicies: state.userAccessPolicyState.userAccessPolicies,
  reduxErrors: [
    state.poDetailsState.error,
    state.poListState.error,
  ],
});

const mapDispatchToProps = (dispatch): IAppDispatchOwnProps => ({
  getUser: () => {
    dispatch(getUserRequest());
  },
  getFeatureFlags: (userProfile: UserProfile) => dispatch(getFeatureFlags(userProfile)),
});

export const ConnectedApp = connect(mapStateToProps, mapDispatchToProps)(App);
